/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useCallback, useMemo } from 'react';
import {
  Auth0Provider,
  withAuthenticationRequired as withAuth0Required,
  WithAuthenticationRequiredOptions,
} from '@auth0/auth0-react';
import { getConfig } from 'helpers/appConfig';
import { FCWithChildren } from 'helpers/types/FCWithChildren';
import { WebStorageStateStore } from 'oidc-client-ts';
import {
  AuthProvider,
  AuthProviderProps,
  withAuthenticationRequired as withOIDCRequired,
} from 'react-oidc-context';
import { useLocation, useNavigate } from 'react-router-dom';

const config = getConfig();

export enum AuthorizationProvider {
  Null = 1,
  Auth0,
  Keycloak,
}

export const ActiveAuthorizationProvider = AUTH_BYPASS
  ? AuthorizationProvider.Null
  : config.keycloak.enabled
  ? AuthorizationProvider.Keycloak
  : AuthorizationProvider.Auth0;

const Auth0ProtectedRoute: FC<any> = ({ component }) => {
  const { pathname } = useLocation();
  const screen_hint = pathname === '/sign-up' ? 'signup' : 'login';
  const authenticationArgs: WithAuthenticationRequiredOptions = {
    onRedirecting: () => <p>Loading...</p>,
    loginOptions: {
      // screen_hint: screen_hint,
    },
  };

  const Component = useMemo(() => {
    return withAuth0Required(component, authenticationArgs);
    // authentication args are treated as a constant here because
    // they should only ever change if the component itself changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [component]);

  return <Component />;
};

const NullProtectedRoute: FC<any> = ({ component: Component }) => <Component />;

const KeycloakProtectedRoute: FC<any> = ({ component, ...args }) => {
  const { pathname } = useLocation();

  const Component = useMemo(() => {
    const screen_hint = pathname === '/sign-up' ? 'signup' : 'login';

    return withOIDCRequired(component, {
      OnRedirecting: () => <p>Loading...</p>,
      ...args,
      signinRedirectArgs: {
        login_hint: screen_hint,
      },
    });
    // authentication args are treated as a constant here because
    // they should only ever change if the component itself changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [component]);

  return <Component />;
};

const routes: Record<AuthorizationProvider, FC<any>> = {
  [AuthorizationProvider.Auth0]: Auth0ProtectedRoute,
  [AuthorizationProvider.Null]: NullProtectedRoute,
  [AuthorizationProvider.Keycloak]: KeycloakProtectedRoute,
};

export const ProtectedRoute = routes[ActiveAuthorizationProvider];

const KeycloakProviderWithRedirectCallback: FCWithChildren<any> = ({
  children,
}) => {
  const oidcConfig: AuthProviderProps = {
    onSigninCallback: () => {
      // the window.history.replaceState is pushing instead of replacing
      // possibly due to happening so close to when the page loaded
      return new Promise(resolve => {
        setTimeout(() => {
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname,
          );
          resolve();
        }, 500);
      });
    },
    authority: config.keycloak.authority,
    client_id: config.keycloak.clientId,
    redirect_uri: window.location.href,
    automaticSilentRenew: true,
    loadUserInfo: true,
    revokeTokensOnSignout: true,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    // ...
  };
  return <AuthProvider {...oidcConfig}>{children}</AuthProvider>;
};

const NullProviderWithRedirectCallback: FCWithChildren<any> = ({
  children,
}) => <>{children}</>;

const Auth0ProviderWithRedirectCallback: FCWithChildren<any> = ({
  children,
}) => {
  const navigate = useNavigate();
  const onRedirectCallback = useCallback(
    (appState?: { returnTo?: any }) => {
      navigate((appState && appState.returnTo) || window.location.pathname);
    },
    [navigate],
  );

  return (
    <Auth0Provider
      onRedirectCallback={onRedirectCallback}
      domain={config.auth0.domain}
      clientId={config.auth0.clientId}
      useRefreshTokens={true}
      cacheLocation="localstorage"
      authorizationParams={{
        redirect_uri: window.location.origin + '/home',
        audience: config.auth0.audience,
      }}
    >
      {children}
    </Auth0Provider>
  );
};

const providers: Record<AuthorizationProvider, FCWithChildren<any>> = {
  [AuthorizationProvider.Null]: NullProviderWithRedirectCallback,
  [AuthorizationProvider.Keycloak]: KeycloakProviderWithRedirectCallback,
  [AuthorizationProvider.Auth0]: Auth0ProviderWithRedirectCallback,
};

export const AuthProviderWithRedirectCallback =
  providers[ActiveAuthorizationProvider];
